import { useMemo } from 'react';
import { useMe } from '@crehana/auth';
import ExternalLink from '@crehana/compass.icons.outline/ExternalLink';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { notEmpty } from '@crehana/utils';
import { FEATURE_FLAGS_V5 } from "../../../../../CrossPlatformNavigation/cross-platform-navigation-v2/feature-flags";
import { useFlagOrganization } from "../../../../../hooks/useFlagOrganization";
import { useProfileUITranslations } from "../../i18n";
import { useLeadershipByCurrentSession } from "../use-leadership-by-current-session";
import useLeadership from "./useLeadership";
import useOrganizationsList from "./useOrganizationsList";
import { getDefaultSelectedRole, getUserRoleLabel, getUserRoles } from "./utils";
var useProfileData = function useProfileData(_ref) {
  var _me$organization, _me$organization2, _me$organization3, _me$organization4;
  var organizationSlug = _ref.organizationSlug,
    _ref$currentUrl = _ref.currentUrl,
    currentUrl = _ref$currentUrl === void 0 ? '/org/crehana/learn' : _ref$currentUrl,
    customCollaboratorUrl = _ref.customCollaboratorUrl,
    _ref$hideLeaderItemIn = _ref.hideLeaderItemInRoleSelector,
    hideLeaderItemInRoleSelector = _ref$hideLeaderItemIn === void 0 ? false : _ref$hideLeaderItemIn,
    _ref$hideLeaderOrSupe = _ref.hideLeaderOrSupervisorInNavbarLabel,
    hideLeaderOrSupervisorInNavbarLabel = _ref$hideLeaderOrSupe === void 0 ? true : _ref$hideLeaderOrSupe;
  var _useMe = useMe(),
    me = _useMe.me,
    talentUser = _useMe.talentUser;
  var _useProfileUITranslat = useProfileUITranslations(),
    t = _useProfileUITranslat.t;
  var _useLeadership = useLeadership({
      organizationSlug: organizationSlug
    }),
    isLeader = _useLeadership.isLeader;
  var _useLeadershipByCurre = useLeadershipByCurrentSession({
      skip: !(me !== null && me !== void 0 && me.originalId) || hideLeaderOrSupervisorInNavbarLabel
    }),
    isCentralizedSupervisor = _useLeadershipByCurre.isSupervisor,
    isCentralizedLeader = _useLeadershipByCurre.isLeader;
  var talentSuitFlag = useFlagOrganization({
    organizationSlug: organizationSlug,
    flag: 'talent-suite'
  });
  var _useFeatureFlag = useFeatureFlag(FEATURE_FLAGS_V5.HIDE_HRIS_MODULES),
    hideHRISModules = _useFeatureFlag.flagValue;
  var links = [{
    key: 'profile',
    url: me === null || me === void 0 ? void 0 : me.profileUrl,
    label: t('PROFILE_PROFILE')
  }, {
    key: 'manage-account',
    url: '/configuracion/',
    label: t('PROFILE_MANAGE_ACCOUNT')
  }, me !== null && me !== void 0 && me.isB2BAdmin ? {
    key: 'help-center',
    url: 'https://ayuda.crehana.com/',
    label: t('PROFILE_HELP_CENTER'),
    openInNewTab: true,
    adornment: {
      icon: ExternalLink
    }
  } : null].filter(notEmpty);
  var hrisLinks = [{
    key: 'profile',
    url: "/org/".concat(organizationSlug, "/profile/my-profile/information/"),
    label: t('PROFILE_PROFILE')
  }, {
    key: 'manage-account',
    url: "/org/".concat(organizationSlug, "/profile/settings/"),
    label: t('PROFILE_MANAGE_ACCOUNT')
  }, me !== null && me !== void 0 && me.isB2BAdmin ? {
    key: 'help-center',
    url: 'https://ayuda.crehana.com/',
    label: t('PROFILE_HELP_CENTER'),
    openInNewTab: true,
    adornment: {
      icon: ExternalLink
    }
  } : null].filter(notEmpty);
  var _useOrganizationsList = useOrganizationsList({
      corporationName: (me === null || me === void 0 ? void 0 : (_me$organization = me.organization) === null || _me$organization === void 0 ? void 0 : _me$organization.name) || '',
      corporationSlug: (me === null || me === void 0 ? void 0 : (_me$organization2 = me.organization) === null || _me$organization2 === void 0 ? void 0 : _me$organization2.slug) || '',
      currentSlug: organizationSlug,
      skip: (me === null || me === void 0 ? void 0 : (_me$organization3 = me.organization) === null || _me$organization3 === void 0 ? void 0 : _me$organization3.myRole) !== 'owner-corporation'
    }),
    organizationsList = _useOrganizationsList.organizationsList,
    defaultSelectedOrganization = _useOrganizationsList.defaultSelectedOrganization;
  var shouldRenderNightSwitch = !(talentSuitFlag !== null && talentSuitFlag !== void 0 && talentSuitFlag.isActive);
  var canRenderCompaniesDropdown = Boolean((me === null || me === void 0 ? void 0 : (_me$organization4 = me.organization) === null || _me$organization4 === void 0 ? void 0 : _me$organization4.myRole) === 'owner-corporation' && (organizationsList === null || organizationsList === void 0 ? void 0 : organizationsList.length) > 1);
  var userRoles = getUserRoles({
    slug: organizationSlug,
    user: me,
    isLeader: isLeader && !hideLeaderItemInRoleSelector,
    talentUser: talentUser,
    t: t,
    customCollaboratorUrl: customCollaboratorUrl
  });
  var defaultSelectedRole = userRoles.length > 0 ? getDefaultSelectedRole({
    userRoles: userRoles,
    currentUrl: currentUrl
  }) : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  var userRoleLabel = useMemo(function () {
    if (!hideLeaderOrSupervisorInNavbarLabel) {
      if (isCentralizedSupervisor) {
        return t(getUserRoleLabel('supervisor'));
      } else if (isCentralizedLeader) {
        return t(getUserRoleLabel('leader'));
      }
    }
    var userRoleLabelKey = getUserRoleLabel(defaultSelectedRole === null || defaultSelectedRole === void 0 ? void 0 : defaultSelectedRole.value);
    return userRoleLabelKey ? t(userRoleLabelKey) : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedRole === null || defaultSelectedRole === void 0 ? void 0 : defaultSelectedRole.value, hideLeaderOrSupervisorInNavbarLabel, isCentralizedLeader, isCentralizedSupervisor]);
  return {
    slug: organizationSlug,
    userRoleLabel: userRoleLabel,
    userRoles: userRoles,
    defaultSelectedRole: defaultSelectedRole,
    me: {
      username: (me === null || me === void 0 ? void 0 : me.username) || '',
      fullName: (me === null || me === void 0 ? void 0 : me.fullName) || '',
      email: (me === null || me === void 0 ? void 0 : me.email) || '',
      profileUrl: (me === null || me === void 0 ? void 0 : me.profileUrl) || '',
      thumbnailUrl: (me === null || me === void 0 ? void 0 : me.thumbnailUrl) || ''
    },
    shouldRenderNightSwitch: shouldRenderNightSwitch,
    canRenderCompaniesDropdown: canRenderCompaniesDropdown,
    organizationsList: organizationsList,
    defaultSelectedOrganization: defaultSelectedOrganization,
    links: !hideHRISModules ? hrisLinks : links
  };
};
export default useProfileData;